import React, { useState } from "react";
import styled from "styled-components";
import ChatPanel from "./ChatPanel";
import useChatLogic from "./useChatLogic";

/**
 * PortfolioAIChat
 * フローティングボタン + チャットパネルのコンテナ
 *
 * Props:
 * - portfolios: Map（現在のポートフォリオ状態）
 * - amData: Map（資産タイプ×プロダクト情報）
 * - productProps: Object（プロダクト情報マップ）
 * - assetNames: Map（資産タイプ表示名マップ）
 * - portfolioSetId: string
 * - workspaceId: string
 * - onApplyActions: function（AI提案を適用するコールバック）
 */
const PortfolioAIChat = ({
  portfolios,
  amData,
  productProps,
  assetNames,
  analysisResult,
  portfolioSetId,
  workspaceId,
  onApplyActions,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    messages,
    pendingActions,
    isLoading,
    error,
    sendMessage,
    applyActions,
    cancelActions,
    resetChat,
  } = useChatLogic({
    portfolios,
    amData,
    productProps,
    assetNames,
    analysisResult,
    onApplyActions,
  });

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <FloatingContainer>
      {/* チャットパネル（展開時） */}
      {isOpen && (
        <PanelWrapper>
          <ChatPanel
            messages={messages}
            pendingActions={pendingActions}
            isLoading={isLoading}
            onSendMessage={sendMessage}
            onApplyActions={applyActions}
            onCancelActions={cancelActions}
            onReset={resetChat}
            assetNames={assetNames}
          />
        </PanelWrapper>
      )}

      {/* フローティングボタン */}
      <FloatingButton onClick={handleToggle} $isOpen={isOpen}>
        {isOpen ? (
          <span style={{ fontSize: "18px" }}>✕</span>
        ) : (
          <>
            <span style={{ fontSize: "20px" }}>🤖</span>
            {/* 未読バッジ（ローディング中や提案待ち時に表示） */}
            {(isLoading || pendingActions.length > 0) && (
              <Badge>{pendingActions.length > 0 ? "!" : "…"}</Badge>
            )}
          </>
        )}
      </FloatingButton>

      {/* ツールチップ（パネルが閉じている時） */}
      {!isOpen && <Tooltip>AIアシスタント</Tooltip>}
    </FloatingContainer>
  );
};

const FloatingContainer = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;

  /* ツールチップを表示するためのhoverグループ */
  &:hover > div:last-child {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PanelWrapper = styled.div`
  width: 420px;
  height: calc(100vh - 100px);
  max-height: calc(100vh - 100px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(25, 46, 85, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: slideUp 0.2s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FloatingButton = styled.button`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background-color: ${({ $isOpen }) => ($isOpen ? "#546e7a" : "#2397ce")};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(35, 151, 206, 0.4), 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: background-color 0.2s ease, transform 0.15s ease,
    box-shadow 0.2s ease;
  position: relative;
  flex-shrink: 0;

  &:hover {
    background-color: ${({ $isOpen }) => ($isOpen ? "#455a64" : "#1a7aaa")};
    transform: scale(1.06);
    box-shadow: 0 6px 20px rgba(35, 151, 206, 0.5), 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.96);
  }
`;

const Badge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #f44336;
  color: white;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 58px;
  right: 0;
  background-color: #192e55;
  color: white;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    right: 18px;
    border: 5px solid transparent;
    border-top-color: #192e55;
  }
`;

export default PortfolioAIChat;
